import type { YuzuBlock, YuzuTemplatePage } from '@giftie/yuzu-integration'

export type YuzuMutationAction = 'select' | 'edit' | 'move' | 'resize' | 'duplicate' | 'delete'

/** Only explicitly dynamic image slots may expose shopper replacement controls. */
export function isYuzuImageReplaceable(block: YuzuBlock) {
  return block.type === 'image'
    && block.attributes.dynamicContentEnabled === true
}

/** Storefront-hidden blocks stay in the schema but are omitted from the builder canvas. */
export function isYuzuBlockStorefrontVisible(block: YuzuBlock) {
  return block.attributes.storefrontVisibility !== false
}

/** Returns whether a block or one of its ancestors is locked. */
export function isYuzuBlockEffectivelyLocked(
  block: YuzuBlock,
  blockMap: Map<string, YuzuBlock>,
): boolean {
  if (block.attributes.locked === true) return true
  if (!block.parent) return false

  const parent = blockMap.get(block.parent)
  return parent ? isYuzuBlockEffectivelyLocked(parent, blockMap) : false
}

/** Central policy guard used by every supported v2 block mutation. */
export function canMutateYuzuBlock(
  block: YuzuBlock,
  blockMap: Map<string, YuzuBlock>,
  action: YuzuMutationAction,
): boolean {
  if (
    block.type === 'image'
    && (action === 'select' || action === 'edit')
    && !isYuzuImageReplaceable(block)
    && block.attributes.giftieDecoration !== true
  ) return false

  if (!isYuzuBlockEffectivelyLocked(block, blockMap)) return true

  // Locking freezes block geometry and structure. It does not freeze the
  // shopper-editable copy inside a text or handwriting block, including copy
  // nested below a locked container.
  return isYuzuShopperCopyBlock(block) && (action === 'select' || action === 'edit')
}

/** Blocks whose first child is shopper-editable message copy. */
export function isYuzuShopperCopyBlock(block: YuzuBlock) {
  return block.type === 'text' || block.type === 'handwriting'
}

/** Builds a lookup map without changing the recursive Yuzu block tree. */
export function createYuzuBlockMap(pages: YuzuTemplatePage[]): Map<string, YuzuBlock> {
  const entries = pages.flatMap((page) => collectBlockEntries(page.blocks))
  return new Map(entries)
}

/** Collects recursive block entries for lookup construction. */
function collectBlockEntries(blocks: YuzuBlock[]): Array<[string, YuzuBlock]> {
  return blocks.flatMap((block) => [
    [block.id, block] as [string, YuzuBlock],
    ...collectBlockEntries(block.children.filter((child): child is YuzuBlock => typeof child === 'object')),
  ])
}
